import { useEffect } from "react";
import { MessageCircle, Mail, MapPin, Clock } from "lucide-react";
import PageHeader from "../components/layout/PageHeader";
import { setPageMeta, JsonLd, breadcrumbSchema } from "../lib/schema";
import { buildWhatsAppLink } from "../lib/whatsapp";

export default function ContactPage() {
  useEffect(() => {
    setPageMeta({
      title: "Contact My Private Shopper | UK to Nigeria",
      description:
        "Reach the MPS concierge team — WhatsApp, email, or via our enquiry form. London-based, serving Nigeria.",
      canonical: "https://privateshopper.co.uk/contact",
    });
  }, []);

  return (
    <>
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Contact" },
      ])} />
      <PageHeader
        overline="Contact"
        title={<>A real concierge, <span className="italic text-[#C9A96E]">always one tap away</span>.</>}
        body="WhatsApp is the fastest route — expect a reply in minutes during UK working hours, within a few hours overnight."
      />

      <section className="container-mps py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="bg-[#0A0A0A] text-[#F8F5F0] p-10 md:p-14" data-testid="contact-card-whatsapp">
            <MessageCircle className="text-[#C9A96E] mb-5" size={32} strokeWidth={1.4} />
            <div className="overline text-[#C9A96E] mb-2">Preferred Channel</div>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">WhatsApp</h2>
            <p className="text-[#F8F5F0]/70 leading-relaxed mb-8">
              Send a product link, a screenshot, or just a description. Our team
              will quote and confirm your order over chat.
            </p>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-cta"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-[#0A0A0A] text-sm uppercase tracking-[0.18em] hover:bg-[#1EBE5C]"
            >
              <MessageCircle size={16} /> +44 7823 718667
            </a>
          </div>

          <div className="bg-[#F8F5F0] border border-[#E5E0D8] p-10 md:p-14" data-testid="contact-card-email">
            <Mail className="text-[#C9A96E] mb-5" size={32} strokeWidth={1.4} />
            <div className="overline text-[#C9A96E] mb-2">Email</div>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">ops@privateshopper.co.uk</h2>
            <p className="text-[#6B7280] leading-relaxed mb-8">
              For business enquiries, retainers, and anything that benefits from
              attached documents. We reply within one working day.
            </p>
            <a
              href="mailto:ops@privateshopper.co.uk"
              data-testid="contact-email-cta"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#1A1A1A] text-sm uppercase tracking-[0.18em] hover:bg-[#0A0A0A] hover:text-[#F8F5F0]"
            >
              <Mail size={16} /> Send Email
            </a>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
          <InfoBlock icon={MapPin} title="Based in" body="London, United Kingdom" />
          <InfoBlock icon={Clock} title="Working hours" body="Mon – Sat · 9:00 – 19:00 GMT" />
          <InfoBlock icon={Mail} title="Email" body="ops@privateshopper.co.uk" />
        </div>
      </section>
    </>
  );
}

function InfoBlock({ icon: Icon, title, body }) {
  return (
    <div className="p-8 border border-[#E5E0D8]">
      <Icon className="text-[#C9A96E] mb-4 mx-auto md:mx-0" size={22} strokeWidth={1.4} />
      <div className="overline text-[#6B7280]">{title}</div>
      <div className="mt-2 text-lg text-[#1A1A1A]">{body}</div>
    </div>
  );
}
